import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Bell, CheckCheck, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useNotifications } from '../hooks/useNotifications'
import { cn } from '@/lib/utils'
import type { Notification, NotificationType } from '@/types'

// Get icon/color for notification type
function getNotificationStyle(type: NotificationType): { icon: string; color: string; bg: string } {
  switch (type) {
    case 'deposit_approved':
      return { icon: '💰', color: 'text-green-400', bg: 'bg-green-500/20' }
    case 'deposit_rejected':
      return { icon: '❌', color: 'text-red-400', bg: 'bg-red-500/20' }
    case 'withdrawal_approved':
      return { icon: '💸', color: 'text-green-400', bg: 'bg-green-500/20' }
    case 'withdrawal_rejected':
      return { icon: '❌', color: 'text-red-400', bg: 'bg-red-500/20' }
    case 'kyc_approved':
      return { icon: '✅', color: 'text-green-400', bg: 'bg-green-500/20' }
    case 'kyc_rejected':
      return { icon: '❌', color: 'text-red-400', bg: 'bg-red-500/20' }
    case 'welcome':
      return { icon: '🎉', color: 'text-casino-gold', bg: 'bg-casino-gold/20' }
    case 'system':
    default:
      return { icon: '📢', color: 'text-blue-400', bg: 'bg-blue-500/20' }
  }
}

// Format time ago
function formatTimeAgo(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Ahora'
  if (diffMins < 60) return `hace ${diffMins}m`
  if (diffHours < 24) return `hace ${diffHours}h`
  if (diffDays < 7) return `hace ${diffDays}d`

  return date.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' })
}

// Single notification item
function NotificationItem({
  notification,
  onMarkRead,
}: {
  notification: Notification
  onMarkRead: (id: string) => void
}) {
  const style = getNotificationStyle(notification.type)

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      className={cn(
        'p-3 border-b border-zinc-800 hover:bg-zinc-800/50 transition-colors cursor-pointer',
        !notification.read && 'bg-zinc-800/30'
      )}
      onClick={() => !notification.read && onMarkRead(notification.id)}
    >
      <div className="flex gap-3">
        {/* Icon */}
        <div
          className={cn(
            'w-10 h-10 rounded-full flex items-center justify-center text-lg flex-shrink-0',
            style.bg
          )}
        >
          {style.icon}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className={cn('font-medium text-sm', !notification.read ? 'text-white' : 'text-gray-400')}>
              {notification.title}
            </p>
            {!notification.read && (
              <div className="w-2 h-2 rounded-full bg-casino-gold flex-shrink-0 mt-1.5" />
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notification.message}</p>
          <p className="text-xs text-gray-600 mt-1">{formatTimeAgo(notification.createdAt)}</p>
        </div>
      </div>
    </motion.div>
  )
}

export function NotificationBell() {
  const { notifications, unreadCount, loading, markNotificationAsRead, markAllNotificationsAsRead } =
    useNotifications()
  const [isOpen, setIsOpen] = useState(false)

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5 text-gray-400 hover:text-white transition-colors" />
          {unreadCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold text-white"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </motion.span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-80 max-h-[70vh] overflow-hidden flex flex-col bg-zinc-900 border-zinc-800"
      >
        {/* Header */}
        <div className="p-3 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="font-semibold text-white">Notificaciones</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-casino-gold hover:text-casino-gold/80 h-auto py-1"
              onClick={markAllNotificationsAsRead}
            >
              <CheckCheck className="w-3 h-3 mr-1" />
              Marcar todas leídas
            </Button>
          )}
        </div>

        {/* Notifications list */}
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-casino-gold" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-500">
              <Bell className="w-10 h-10 mb-2 opacity-30" />
              <p className="text-sm">No hay notificaciones</p>
            </div>
          ) : (
            <AnimatePresence>
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkRead={markNotificationAsRead}
                />
              ))}
            </AnimatePresence>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-2 border-t border-zinc-800 text-center">
            <span className="text-xs text-gray-500">
              {notifications.length} notificaciones
            </span>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default NotificationBell
